// Core
import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// Assets
import { apiService } from 'services/ApiService';

// Components
import WheelsEditContent from './components/PartnerEditContent';

import { PLAN_TYPES } from '../../../constants/plan';
import PartnerEditContent from './components/PartnerEditContent';
import useFormPageRedirect from '../../../hooks/useFormPageRedirect';

const partnerSchema = z.object({
  name: z.string().min(1),
  email: z.string().min(1),
  phone: z.string().min(1),
  locationAddress: z.string().min(1),
});

const PartnersEdit = () => {
  const history = useHistory();
  const { id } = useParams();

  const [errorCard, setErrorCard] = useState();

  const form = useForm({
    resolver: zodResolver(partnerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      locationAddress: '',
    },
  });

  const { reset, handleSubmit } = form;

  const { canRead } = useFormPageRedirect({
    id,
    createPermissionKey: 'POST:/admin/partners',
    readPermissionKey: 'GET:/admin/partners/:id',
    redirectPath: '/partners',
  });

  const { isLoading, isError } = useQuery({
    queryKey: ['partner', id, canRead],
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnError: false,
    queryFn: async () => {
      if (id === 'new') {
        reset({
          name: '',
          email: '',
          phone: '',
          locationAddress: '',
        });
        return true;
      }
      if (canRead) {
        const response = await apiService.getPartner({ id });
        reset(response.data);
      }
      return true;
    },
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (id === 'new') {
        return apiService.createPartner(data);
      } else {
        return apiService.updatePartner({ id, ...data });
      }
    },
    onSuccess: (res) => {
      if (
        res.data === false ||
        !(res.statusCode >= 200 && res.statusCode < 300)
      ) {
        setErrorCard(res.message);
        return;
      }
      history.push('/partners');
    },
    onError: () => {
      setErrorCard('Something went wrong.');
    },
  });

  const onSubmit = (values) => {
    mutation.mutate(values);
  };

  const onError = (errors) => {
    console.log('Validation errors:', errors);
  };

  const handleDelete = useCallback(async () => {
    await apiService.deletePartner({ id });
    reset({
      name: '',
      email: '',
      phone: '',
      locationAddress: '',
    });
    history.push('/partners');
  }, [history, id, reset]);

  return isError ? (
    <div>Not Found</div>
  ) : isLoading ? (
    <div>Loading...</div>
  ) : (
    <PartnerEditContent
      handleDelete={handleDelete}
      isNew={id === 'new'}
      form={form}
      onSubmit={handleSubmit(onSubmit, onError)}
      isSubmitting={mutation.isLoading}
      errorCard={errorCard}
    />
  );
};

export default PartnersEdit;
